import { CountryCode, parsePhoneNumber } from 'libphonenumber-js';
import React, { useEffect, useMemo } from 'react';
import { Control, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'utils/general/storage';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { useGetCompanyJobTitles } from '@/modules/auth/apiHooks';
import { employeesNumberList } from '@/modules/auth/components/FormsContainer/CreateCompany/logic';
import { useSignup } from '@/modules/auth/components/FormsContainer/Signup/logic';
import { IAuth } from '@/modules/auth/types';
import {
  InputField,
  PhoneInputWithCountry,
  SelectField,
  Typography,
} from '@/shared/components/atoms';
import { useGetCountries } from '@/shared/hooks/apiHooks';
import { validatePhoneNumber } from '@/shared/lib/libphonenumber';
import { IGeneral } from '@/shared/types';

function CompanyFields() {
  const { t } = useTranslation('common');

  // hooks
  const { getCookie } = useCookies();
  const { getCompanyCountryInfo } = useSignup();
  const { data: countries } = useGetCountries();
  const { data: jobTitles } = useGetCompanyJobTitles();

  const countryCode = useMemo(
    () => getCookie('defaultCountryCode') ?? 'EG',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    formState: { errors },
    register,
    control,
    watch,
    reset,
    setValue,
    getValues,
  } = useFormContext<IAuth.ICompleteSignupForm>();

  // Watchers
  const phoneNumberWatcher = watch('phoneNumber');

  // update form on phone number change
  useEffect(() => {
    if (validatePhoneNumber(phoneNumberWatcher, true)) {
      const countryCode = parsePhoneNumber(
        phoneNumberWatcher as string
      )?.country;
      const countryData = countries?.find(
        (country) => country.isoCode === countryCode
      );
      if (countryData) {
        reset({
          ...getValues(),
          ...getCompanyCountryInfo(countryData),
        });
      }
    } else {
      reset({
        ...getValues(),
        ...getCompanyCountryInfo(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, phoneNumberWatcher]);

  return (
    <div className='overflow-auto px-24 pt-0 pb-24 desktop:px-148'>
      <div className='flex items-center justify-between gap-16'>
        <div>
          <Typography weight='bold' variant='h6'>
            {t('digitize-your-farm')}
          </Typography>
          <Typography variant='subtitle2' className='text-surface-10'>
            {t('enter-basic-information')}
          </Typography>
        </div>
      </div>

      {/* <ScrollableWithShadow
        className='mt-32'
        maxHeight='max-h-[60vh]'
        bgColor='light-grey'
      >
         */}
      <div className='mb-2 mt-7 flex flex-col gap-16'>
        <div className='flex gap-7'>
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-12'
            label={t('First Name')}
            placeholder={t('First Name')}
            error={!!errors.firstName}
            errorMessage={errors.firstName?.message}
            className='!rounded-xl bg-background-transparent'
            {...register('firstName')}
            required
          />
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-12'
            label={t('Last Name')}
            placeholder={t('Last Name')}
            error={!!errors.lastName}
            errorMessage={errors.lastName?.message}
            className='!rounded-xl bg-background-transparent'
            {...register('lastName')}
            required
          />
        </div>
        <InputField
          borderColor='border-outline-variant'
          width='w-full'
          py='!py-12'
          label={t('Company Name')}
          placeholder={t('Company Name')}
          error={!!errors.companyName}
          errorMessage={errors.companyName?.message}
          className='!rounded-xl bg-background-transparent'
          {...register('companyName')}
          required
        />
        <div>
          <PhoneInputWithCountry
            defaultCountry={countryCode as CountryCode}
            onCountryChange={(country) => setValue('country', country)}
            borderColor='border-outline-variant border-xs rounded-xl'
            py='py-[3px]'
            width='w-full'
            label={t('phone-number')}
            placeholder={t('phone-number')}
            id='phoneNumber'
            name='phoneNumber'
            error={!!errors.phoneNumber}
            errorMessage={errors.phoneNumber?.message?.toString()}
            control={control as unknown as Control<FieldValues>}
            required
          />
        </div>
        <SelectField<string, false>
          id='noOfEmployees'
          getLabel={(item: IGeneral.NameIdPair) =>
            t(item.name, { ns: 'login' })
          }
          getValue={(item: IGeneral.NameIdPair) => item.name}
          className='!rounded-xl bg-background-transparent'
          borderColor={themeColors().outline.variant}
          label={t('emplyees-number-question', {
            ns: 'login',
          })}
          placeholder={t('emplyees-number-placeholder', {
            ns: 'login',
          })}
          list={employeesNumberList}
          py='6px'
          px='0.5rem'
          borderRadius='1rem'
          width='w-full'
          maxMenuHeight={190}
          menuPlacement='top'
          required
        />
        <SelectField
          id='jobTitle'
          getLabel={(obj: IAuth.IJobTitle) => t(obj.name)}
          getValue={(obj: IAuth.IJobTitle) => obj.defaultName}
          className='!rounded-xl bg-background-transparent'
          borderColor={themeColors().outline.variant}
          label={t('job-title-question')}
          placeholder={t('select-role')}
          list={jobTitles?.jobTitles ?? []}
          py='6px'
          px='0.5rem'
          borderRadius='1rem'
          width='w-full'
          maxMenuHeight={190}
          menuPlacement='top'
          required
        />
      </div>
      {/* </ScrollableWithShadow> */}
    </div>
  );
}

export { CompanyFields };
